import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { formatCurrency, getDetails } from '@paintscout/util/builder'
import classnames from 'classnames'
import React, { useMemo } from 'react'
import { useQuote } from '../../context'

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      minWidth: 0
    },
    label: {
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 500,
      lineHeight: '14px',
      color: theme.paintscout.palette.brand.Neutral.P60,
      whiteSpace: 'nowrap'
    },
    value: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 600,
      lineHeight: '20px',
      color: theme.paintscout.palette.brand.Neutral.P35,
      whiteSpace: 'nowrap'
    }
  }),
  { name: 'MobileNavTotal' }
)

export interface MobileNavTotalProps {
  className?: string
}

/**
 * The estimate total stacked beside Accept in the customer's phone nav bar (ENG-2075, matches the
 * prototype). Shows the after-tax total, as the Quick Cart drawer does. Shows nothing on an invoice
 * (the prototype only covers estimates), or when the account or the estimate hides its totals.
 */
export default function MobileNavTotal({ className }: MobileNavTotalProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { quote } = useQuote()
  const details = useMemo(() => getDetails({ quote, options }), [quote, options])

  const value = quote?.totals?.after_tax
  if (quote?.is_invoice || options.options.quotes?.hideTotalOnPresentation || !details.totals.show || !value) {
    return null
  }

  return (
    <div className={classnames(classes.root, className)}>
      <Typography component="span" className={classes.label}>
        Total
      </Typography>
      <Typography component="span" className={classes.value}>
        {formatCurrency({ options, value })}
      </Typography>
    </div>
  )
}
